import React, { useState, useEffect } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Paper,
  Typography,
  Button,
} from "@mui/material";
import { getAllClassrooms } from "../../api/sponteAPI";
import { getSubjects } from "../../api/sponteAPI";
import { anoLetivo } from "../../utils/YEARCONSTS";

/**
 * Componente para seleção de turmas e disciplinas de um professor
 * Permite adicionar múltiplas combinações de turma + disciplina
 */
export function TeacherAllocationsForm({ allocations, onChange }) {
  const [classrooms, setClassrooms] = useState([]);
  const [selectedTurma, setSelectedTurma] = useState("");
  const [selectedDisciplina, setSelectedDisciplina] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(false);

  // Carregar turmas disponíveis
  useEffect(() => {
    const loadClassrooms = async () => {
      try {
        setLoading(true);
        const data = await getAllClassrooms(anoLetivo);
        
        // Normalizar dados - a API Sponte pode retornar arrays dentro dos objetos
        const normalized = (data || []).map(classroom => {
          // Normalizar TurmaID
          let turmaID = classroom.TurmaID;
          if (Array.isArray(turmaID)) {
            turmaID = turmaID[0];
          }
          turmaID = typeof turmaID === 'number' ? turmaID : parseInt(turmaID || classroom.id, 10);
          
          // Normalizar Nome
          let nome = classroom.Nome;
          if (Array.isArray(nome)) {
            nome = nome[0];
          }
          nome = nome || classroom.name || '';
          
          return {
            ...classroom,
            TurmaID: turmaID,
            Nome: nome,
            id: turmaID, // Garantir que id também seja o TurmaID normalizado
          };
        });
        
        setClassrooms(normalized);
      } catch (error) {
        console.error("Erro ao carregar turmas:", error);
      } finally {
        setLoading(false);
      }
    };

    loadClassrooms();
  }, []);

  // Carregar disciplinas quando uma turma é selecionada
  useEffect(() => {
    const loadSubjects = async () => {
      // Garantir que selectedTurma seja um valor único (string), não array
      let turmaValue = selectedTurma;
      if (Array.isArray(selectedTurma)) {
        turmaValue = selectedTurma[0];
        // Se o estado está como array, corrigir imediatamente
        if (turmaValue) {
          setSelectedTurma(String(turmaValue));
        }
      }
      
      // Garantir que seja string
      turmaValue = turmaValue ? String(turmaValue) : "";
      
      if (!turmaValue || turmaValue === "") {
        setSubjects([]);
        setSelectedDisciplina([]);
        return;
      }

      try {
        setLoading(true);
        // Converter string para número para a API
        const turmaId = parseInt(turmaValue, 10);
        
        if (!turmaId || isNaN(turmaId)) {
          console.error("TurmaID inválido:", turmaValue);
          setSubjects([]);
          return;
        }

        const data = await getSubjects(turmaId);
        
        // Normalizar disciplinas - o endpoint já retorna data.data
        const normalized = (data || []).map((subj) => ({
          id: subj.DisciplinaID || subj.id,
          name: subj.Nome || subj.name,
        }));
        
        setSubjects(normalized);
      } catch (error) {
        console.error("Erro ao carregar disciplinas:", error);
        setSubjects([]);
      } finally {
        setLoading(false);
      }
    };

    loadSubjects();
  }, [selectedTurma]);

  const handleAddAllocation = () => {
    // Garantir que selectedTurma seja um valor único (string), não array
    let turmaIdStr;
    if (Array.isArray(selectedTurma)) {
      turmaIdStr = String(selectedTurma[0] || "");
    } else {
      turmaIdStr = selectedTurma ? String(selectedTurma) : "";
    }
    
    if (!turmaIdStr || turmaIdStr === "" || (!selectedDisciplina || selectedDisciplina.length === 0)) {
      return;
    }

    // Converter string para número para buscar na lista
    const turmaIdNum = parseInt(turmaIdStr, 10);
    
    if (isNaN(turmaIdNum)) {
      return;
    }
    
    // Tentar encontrar a turma de várias formas
    const turma = classrooms.find((c) => {
      const cTurmaID = c.TurmaID || c.id;
      const cTurmaIDNum = typeof cTurmaID === 'number' ? cTurmaID : parseInt(String(cTurmaID), 10);
      return cTurmaIDNum === turmaIdNum;
    });

    if (!turma) {
      return;
    }

    // Normalizar TurmaID - pode vir como array ou valor único
    let turmaIdNormalized;
    if (Array.isArray(turma.TurmaID)) {
      turmaIdNormalized = turma.TurmaID[0];
    } else {
      turmaIdNormalized = turma.TurmaID || turma.id;
    }
    turmaIdNormalized = typeof turmaIdNormalized === 'number' ? turmaIdNormalized : parseInt(turmaIdNormalized, 10);
    
    // Normalizar turmaName - pode vir como array
    let turmaNameNormalized;
    if (Array.isArray(turma.Nome)) {
      turmaNameNormalized = turma.Nome[0];
    } else if (Array.isArray(turma.name)) {
      turmaNameNormalized = turma.name[0];
    } else {
      turmaNameNormalized = turma.Nome || turma.name || '';
    }
    
    const newAllocations = [];
    // Garantir que selectedDisciplina seja um array
    const disciplinaArray = Array.isArray(selectedDisciplina) ? selectedDisciplina : [selectedDisciplina];
    
    disciplinaArray.forEach((disciplinaId) => {
      const disciplinaIdNum = typeof disciplinaId === 'number' ? disciplinaId : parseInt(disciplinaId, 10);
      const disciplina = subjects.find((s) => {
        const sId = typeof s.id === 'number' ? s.id : parseInt(s.id, 10);
        return sId === disciplinaIdNum;
      });
      
      if (disciplina) {
        // Normalizar DisciplinaID
        const disciplinaIdToSave = typeof disciplina.id === 'number' ? disciplina.id : parseInt(disciplina.id, 10);
        
        // Verificar se já existe essa alocação
        const exists = allocations.some(
          (a) => {
            const aTurmaID = Array.isArray(a.TurmaID) ? a.TurmaID[0] : a.TurmaID || a.turmaID;
            const aTurmaIDNum = typeof aTurmaID === 'number' ? aTurmaID : parseInt(aTurmaID, 10);
            const aDisciplinaID = typeof a.DisciplinaID === 'number' ? a.DisciplinaID : parseInt(a.DisciplinaID || a.disciplinaID, 10);
            return aTurmaIDNum === turmaIdNormalized && aDisciplinaID === disciplinaIdToSave;
          }
        );
        
        if (!exists) {
          newAllocations.push({
            TurmaID: turmaIdNormalized,
            turmaName: turmaNameNormalized,
            DisciplinaID: disciplinaIdToSave,
            disciplinaName: disciplina.name || '',
          });
        }
      }
    });

    if (newAllocations.length > 0) {
      onChange([...allocations, ...newAllocations]);
      // Limpar apenas as disciplinas selecionadas, mantendo a turma para facilitar adicionar mais disciplinas
      setSelectedDisciplina([]);
    }
  };

  const handleRemoveAllocation = (index) => {
    const newAllocations = allocations.filter((_, i) => i !== index);
    onChange(newAllocations);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Turmas e Disciplinas do Professor
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Selecione as turmas e disciplinas que este professor leciona
      </Typography>

      {/* Primeira linha: Seleção de Turma */}
      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <FormControl sx={{ minWidth: 300, flex: 1 }}>
          <InputLabel>Turma</InputLabel>
          <Select
            value={(() => {
              // Garantir que o value seja sempre uma string, não array ou número
              let val = selectedTurma;
              if (Array.isArray(val)) {
                val = val[0];
              }
              // Converter para string se for número
              return val ? String(val) : "";
            })()}
            onChange={(e) => {
              const value = e.target.value;
              // Garantir que seja um valor único (string), não array
              // Se vier como array, pegar o primeiro valor
              let normalizedValue = Array.isArray(value) ? value[0] : value;
              // Garantir que seja string
              normalizedValue = normalizedValue ? String(normalizedValue) : "";
              setSelectedTurma(normalizedValue);
              setSelectedDisciplina([]); // Limpar disciplinas ao mudar turma
              setSubjects([]); // Limpar lista de disciplinas
            }}
            label="Turma"
            disabled={loading}
          >
            <MenuItem value="">Selecione uma turma</MenuItem>
            {classrooms.map((classroom) => {
              const turmaId = classroom.TurmaID || classroom.id;
              // Garantir que o value seja sempre string
              const turmaIdString = String(turmaId);
              return (
                <MenuItem key={turmaIdString} value={turmaIdString}>
                  {classroom.Nome || classroom.name}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </Box>

      {/* Segunda linha: Seleção de Disciplinas e Botão Adicionar */}
      <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: "flex-start" }}>
        <FormControl sx={{ minWidth: 300, flex: 1 }}>
          <InputLabel>Disciplinas</InputLabel>
          <Select
            multiple
            value={selectedDisciplina}
            onChange={(e) => {
              const value = e.target.value;
              // Garantir que value seja sempre um array
              const valueArray = typeof value === 'string' ? [value] : value;
              setSelectedDisciplina(valueArray);
            }}
            label="Disciplinas"
            disabled={!selectedTurma || loading || subjects.length === 0}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 300,
                },
              },
            }}
            renderValue={(selected) => {
              if (!selected || selected.length === 0) {
                return <em style={{ color: '#999' }}>Selecione as disciplinas</em>;
              }
              // Garantir que selected seja um array
              const selectedArray = Array.isArray(selected) ? selected : [selected];
              return (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selectedArray.map((value) => {
                    const disciplina = subjects.find((s) => {
                      const sId = typeof s.id === 'number' ? s.id : parseInt(s.id, 10);
                      const vId = typeof value === 'number' ? value : parseInt(value, 10);
                      return sId === vId;
                    });
                    return (
                      <Chip
                        key={value}
                        label={disciplina?.name || `ID: ${value}`}
                        size="small"
                        onDelete={(e) => {
                          e.stopPropagation();
                          setSelectedDisciplina(selectedArray.filter((v) => v !== value));
                        }}
                      />
                    );
                  })}
                </Box>
              );
            }}
          >
            {subjects.length === 0 ? (
              <MenuItem disabled>
                {!selectedTurma 
                  ? "Selecione uma turma primeiro" 
                  : loading 
                    ? "Carregando disciplinas..." 
                    : "Nenhuma disciplina encontrada"}
              </MenuItem>
            ) : (
              subjects.map((subject) => (
                <MenuItem key={subject.id} value={subject.id}>
                  {subject.name}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          onClick={handleAddAllocation}
          disabled={!selectedTurma || selectedDisciplina.length === 0 || loading}
          sx={{ 
            minWidth: 120,
            height: 56, // Mesma altura do Select
            alignSelf: "flex-start"
          }}
        >
          Adicionar
        </Button>
      </Box>

      {allocations.length > 0 && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Alocações adicionadas:
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {allocations.map((allocation, index) => (
              <Chip
                key={index}
                label={`${allocation.turmaName} - ${allocation.disciplinaName}`}
                onDelete={() => handleRemoveAllocation(index)}
                color="primary"
                variant="outlined"
              />
            ))}
          </Box>
        </Paper>
      )}
    </Box>
  );
}

